import { useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMemberInfoActions, useMemberInfoState } from '../useApi/useMemberInfo';
import { useCareerActions, useCareerState } from '../useApi/useCareer';
import { useMemberInfoStore } from '../../store/memberInfoStore';
import { useAboutViewStore } from '../../store/aboutViewStore';
import { useEditMode } from '../useEditMode';
import { useIsPageOwner } from '../useIsPageOwner';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useDeviceStore } from '../../store/deviceStore';
import { useSettingStore } from '../../store/settingStore';
import { useModalStore } from '../../store/portal/modalStore';
import { useImage } from '../useApi/useImage';
import { isPageOwnerMatchedToRaw } from '../../util/pageOwnerMatch';
import {
  canShowAboutPageSetting,
  canShowCareerList,
  canShowMemberInfo,
  canShowOrderedCareerSections,
} from '../../util/visibility/aboutVisibility';
import type { ResourceScope } from '../../util/scopedResource';
import {
  AboutContentMode,
  AlertPosition,
  AlertType,
  CareerPeriodType,
  CareerType,
  ModalType,
  ServiceType,
} from '../../enum/EnumRepository';
import { UpdateMemberInfoReq } from '../../dto/ReqDtoRepository';
import { careerBuilder } from '../../converter/entityBuilder';
import {
  useAboutPageSettingActions,
  useAboutPageSettingState,
} from '../useApi/useAboutPageSetting';
import { normalizeCareerSectionOrder } from '../../util/careerSectionOrder';
import { toUpdateCareerBulkReq } from '../../util/aboutSave';
import { isValidHttpUrl } from '../../util/urlValidation';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { getAlertMessage } from '../../constants/alertMessages';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';

export function useAboutPage(options?: { load?: boolean }) {
  const load = options?.load ?? false;
  const isOwner = useIsPageOwner();
  const { isEditMode } = useEditMode();
  const { memberInfo, memberInfoOwnerAui, memberInfoScope, status: memberInfoStatus } =
    useMemberInfoState();
  const { getMemberInfo, getMemberInfoOwner, updateMemberInfo, updateEngVisible } =
    useMemberInfoActions();
  const hasChanged = useMemberInfoStore((s) => s.hasChanged);
  const imageChanged = useMemberInfoStore((s) => s.imageChanged);
  const handleChange = useMemberInfoStore((s) => s.updatMemberInfo);
  const { careerList, listOwnerAui, listScope, status: careerStatus } = useCareerState();
  const { getCareerList, getCareerListOwner, createCareer, updateCareerBulk } =
    useCareerActions();
  const {
    aboutPageSetting,
    settingOwnerAui: aboutSettingOwnerAui,
    settingScope: aboutSettingScope,
    status: aboutSettingStatus,
  } = useAboutPageSettingState();
  const { getAboutPageSetting, getAboutPageSettingOwner } =
    useAboutPageSettingActions();
  const { uploadImage } = useImage();
  const { enqueueAlert } = useStandardAlertStore();
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const { deviceType } = useDeviceStore();
  const { setStandardModal } = useModalStore();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const setting = useSettingStore((s) => s.setting);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const { aboutContentMode, setAboutContentMode, isExtended, setIsExtended } =
    useAboutViewStore();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowMember = canShowMemberInfo({
    pageOwnerAui: pageOwner?.aui,
    memberInfoOwnerAui,
    memberInfoScope,
    currentScope,
    status: memberInfoStatus,
  });
  const canShowCareerData = canShowCareerList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status: careerStatus,
  });
  const canShowAboutSetting = canShowAboutPageSetting({
    pageOwnerAui: pageOwner?.aui,
    settingOwnerAui: aboutSettingOwnerAui,
    settingScope: aboutSettingScope,
    currentScope,
    status: aboutSettingStatus,
  });
  const careerSectionOrder = normalizeCareerSectionOrder(
    canShowAboutSetting ? aboutPageSetting?.careerSectionOrder : null,
  );
  const canShowCareer = canShowOrderedCareerSections({
    canShowCareerData,
    canShowAboutSetting,
  });
  const canReorderCareerSections =
    canShowCareer &&
    canShowAboutSetting &&
    new Set(careerList.map((career) => career.careerType)).size > 1;
  const hasCareerChanged = careerList.some((career) => career.hasChanged);
  const hasAboutChanged = hasChanged || imageChanged || hasCareerChanged;
  // owner/scope 불일치 시 stale engVisible로 토글이 숨겨지지 않도록 기본 true
  const engVisible = !canShowMember || memberInfo == null || memberInfo.engVisible !== false;
  const showBothLanguages = canShowMember && memberInfo != null && engVisible;

  useEffect(() => {
    if (!load) return;
    if (useAboutViewStore.getState().aboutContentMode === AboutContentMode.ENG) {
      setAboutContentMode(AboutContentMode.Orig);
    }
    setIsExtended(false);
  }, [load, setAboutContentMode, setIsExtended]);

  // 보기 모드에서 ENG 전환이 숨겨진 경우, ENG가 남지 않게 Orig로 되돌림
  useEffect(() => {
    if (!load) return;
    if (isEditMode || engVisible) return;
    if (aboutContentMode === AboutContentMode.ENG) {
      setAboutContentMode(AboutContentMode.Orig);
    }
  }, [load, isEditMode, engVisible, aboutContentMode, setAboutContentMode]);

  useLayoutEffect(() => {
    if (!load) return;
    const fetchAboutData = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;

      await Promise.all([
        isOwner ? getMemberInfoOwner() : getMemberInfo(),
        isOwner ? getCareerListOwner() : getCareerList(),
        isOwner ? getAboutPageSettingOwner() : getAboutPageSetting(),
      ]);
    };
    fetchAboutData();
  }, [load, raw, pageOwner?.aui, isOwner]);

  const handleEngVisible = async (value: boolean) => {
    if (!memberInfo) return;
    await updateEngVisible({
      id: memberInfo.id,
      updateUploadFileReq: {},
      engVisible: value,
    } as UpdateMemberInfoReq);
  };

  const handleConfirmAll = async () => {
    const careerRequest = toUpdateCareerBulkReq(careerList);
    const hasMemberInfoChanged = hasChanged || imageChanged;

    if (!hasMemberInfoChanged && !careerRequest) return;

    if (careerRequest?.careerList.some(
      (career) => !isValidHttpUrl(career.relatedUrl),
    )) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      return;
    }

    let memberInfoRequest: UpdateMemberInfoReq | null = null;
    if (hasMemberInfoChanged) {
      if (!memberInfo) return;
      const { uploadFile, ...memberInfoWithoutUploadFile } = memberInfo;
      const baseRequest: UpdateMemberInfoReq = {
        ...memberInfoWithoutUploadFile,
        updateUploadFileReq: {
          originUrl: uploadFile.originUrl,
          uploadFileId: uploadFile.id,
          allowDownload: uploadFile.allowDownload,
          width: uploadFile.width,
          height: uploadFile.height,
        },
      };

      const finalRequest = imageChanged
        ? await uploadImage(ServiceType.MEMBER_INFO, baseRequest)
        : baseRequest;
      if (!finalRequest) return;
      memberInfoRequest = finalRequest as UpdateMemberInfoReq;
    }

    const results = await Promise.all([
      ...(memberInfoRequest
        ? [updateMemberInfo(memberInfoRequest, { notify: false })]
        : []),
      ...(careerRequest
        ? [updateCareerBulk(careerRequest, { notify: false })]
        : []),
    ]);

    if (results.every(Boolean)) setUpdatedTempAlert();
  };

  const handleDownloadPDF = () => {
    setStandardModal({
      modalType: ModalType.CREATE_ABOUT_PDF,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const handleDownloadPDFInMobile = () => {
    if (!memberInfo) return;
    handleDownloadPDF();
  };

  const handleCreateElement = async (careerType: CareerType, range: CareerPeriodType) => {
    await createCareer(careerBuilder(careerType, range));
  };

  const handleCreateCareer = () => {
    setStandardModal({
      modalType: ModalType.CREATE_CAREER_MOBILE,
      title: 'Create Career',
      value: null,
      handleChange: () => {},
    });
  };

  const handleReorderCareerSections = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: 'Career Section',
      value: null,
      handleChange: () => {},
    });
  };

  return {
    canShowMember,
    canShowCareer,
    canShowAboutSetting,
    canReorderCareerSections,
    memberInfo,
    careerList,
    careerSectionOrder,
    isEditMode,
    isOwner,
    hasChanged,
    imageChanged,
    hasCareerChanged,
    hasAboutChanged,
    handleChange,
    handleConfirmAll,
    handleEngVisible,
    engVisible,
    showBothLanguages,
    aboutContentMode,
    setAboutContentMode,
    isExtended,
    setIsExtended,
    handleDownloadPDF,
    handleDownloadPDFInMobile,
    handleCreateElement,
    handleCreateCareer,
    handleReorderCareerSections,
    deviceType,
    setting,
  };
}
